import React, { useState, useEffect } from "react";
import { fetchUserDetails } from "services/userService";
import TournamentRegistrationForm from "Components/Registration/TournamentRegistrationForm";
import TournamentParticipantsList from "./TournamentParticipantList";
import {
  toggleRegistrationClosed,
  subscribeToTournamentRegistrations,
  archiveTournament,
} from "services/tournamentService";
import { getMatchesByTournamentId } from "services/matchService";
import { Lock, Unlock, Pencil, Archive, Swords, Trophy } from "lucide-react";
import TournamentPodium from "./tournamentPodium";
import TeamsTables from "./TeamsTables";
import StatsTable from "./StatsTable";
import { Link } from "react-router-dom";
import TournamentTeamManager from "./TourmanentTeamManager";
import PriorityMatches from "./PriorityMatches";
import CollapsibleSection from "Pages/Location/components/CollapsibleSection";
import * as Sentry from "@sentry/react";

const Tournament = ({
  tournament,
  location,
  user,
  users = [],
  locationRankings = [],
  isAdmin = false,
}) => {
  const [userDetails, setUserDetails] = useState({});
  const [registrationClosed, setRegistrationClosed] = useState(
    tournament.registrationClosed
  );
  const [registrations, setRegistrations] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showPodiumModal, setShowPodiumModal] = useState(false);
  const hasMatches = matches.length > 0;

  useEffect(() => {
    const unsubscribe = subscribeToTournamentRegistrations(
      tournament.id,
      setRegistrations
    );
    return () => unsubscribe();
  }, [tournament.id]);

  useEffect(() => {
    const loadUserDetails = async () => {
      if (!isAdmin || registrations.length === 0) return;
      const userIds = registrations.map((reg) => reg.userId);
      const userData = await fetchUserDetails(userIds);
      setUserDetails((prev) => ({ ...prev, ...userData }));
    };
    loadUserDetails();
  }, [registrations, tournament.id, isAdmin]);

  useEffect(() => {
    const checkMatches = async () => {
      try {
        const fetchedMatches = await getMatchesByTournamentId(tournament.id);
        setMatches(fetchedMatches);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error checking matches:", error);
        setMatches([]);
      }
    };

    if (tournament.id) {
      checkMatches();
    }
  }, [tournament.id]);

  const handleToggleRegistration = async () => {
    try {
      const newStatus = await toggleRegistrationClosed(
        tournament.id,
        registrationClosed
      );
      setRegistrationClosed(newStatus);
    } catch (error) {
      Sentry.captureException(error);
      alert("Une erreur s'est produite en changeant le statut d'inscription.");
    }
  };

  const handleArchive = async () => {
    if (window.confirm("Archiver ce tournoi ?")) {
      try {
        await archiveTournament(tournament.id);
        alert("Tournoi archivé.");
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
        alert("Erreur lors de l'archivage.");
      }
    }
  };

  const handleEdit = () => {
    window.location.href = `/locations/tournaments/edit/${tournament.id}`;
  };

  return (
    <div className="flex flex-col w-full bg-slate-900 border border-slate-700 rounded-lg shadow-lg overflow-hidden p-4 gap-4">
      <div className="flex flex-wrap gap-2 justify-between items-start">
        <h3 className="text-xl md:text-2xl font-semibold text-slate-50">
          {tournament.type && tournament.date ? (
            <>
              {tournament.name || tournament.type} —{" "}
              {new Date(tournament.date).toLocaleDateString("fr-FR", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
              <span className="text-amber-400">
                {tournament.time && ` à ${tournament.time}`}
              </span>
            </>
          ) : (
            tournament.name
          )}
        </h3>
        {isAdmin && (
          <div className="flex space-x-2">
            {Object.keys(tournament.teams || {}).length > 0 && (
              <Link
                to={`/locations/tournaments/${tournament.id}/progress`}
                className="flex items-center gap-2 px-2 py-2 sm:px-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md"
                title="Matchs"
              >
                <Swords size={16} /> <span className="hidden sm:inline">Matchs</span>
              </Link>
            )}
            <button
              onClick={() => setShowPodiumModal(true)}
              className="flex items-center gap-2 px-2 py-2 sm:px-3 bg-amber-600 hover:bg-amber-500 text-white rounded-md"
              title="Podium"
            >
              <Trophy size={16} /> <span className="hidden sm:inline">Podium</span>
            </button>
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-2 py-2 sm:px-3 bg-blue-600 hover:bg-blue-500 text-white rounded-md"
              title="Modifier"
            >
              <Pencil size={16} /> <span className="hidden sm:inline">Modifier</span>
            </button>
            <button
              onClick={handleArchive}
              className="flex items-center gap-2 px-2 py-2 sm:px-3 bg-red-600 hover:bg-red-500 text-white rounded-md"
              title="Archiver"
            >
              <Archive size={16} /> <span className="hidden sm:inline">Archiver</span>
            </button>
          </div>
        )}
      </div>

      {location?.allowMyMatches && hasMatches && (
        <Link
          to={`/locations/tournaments/${tournament.id}/my-matches`}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 text-white font-bold rounded-md text-lg transition"
        >
          <Swords size={20} /> Mes matchs
        </Link>
      )}

      {isAdmin && (
        <TournamentPodium
          tournament={tournament}
          showModal={showPodiumModal}
          onCloseModal={() => setShowPodiumModal(false)}
        />
      )}

      {location?.allowMyMatches && (
        <PriorityMatches tournament={tournament} user={user} />
      )}

      {Object.keys(tournament.teams || {}).length > 0 && hasMatches && (
        <StatsTable matches={matches} teams={tournament.teams} tournament={tournament} />
      )}

      <CollapsibleSection title={`Inscriptions — ${registrations.length}/${tournament.waitingListLimit}`} defaultOpen={!hasMatches}>
        <div className="p-4">
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!registrationClosed}
                  onChange={handleToggleRegistration}
                  className="w-4 h-4 accent-yellow-500"
                />
                {registrationClosed ? <Lock size={16} /> : <Unlock size={16} />}
                {registrationClosed ? "Fermées" : "Ouvertes"}
              </label>
            </div>
          )}

          <TournamentRegistrationForm
            tournament={tournament}
            user={user}
            registrations={registrations}
            prefetchedUsers={users}
            isAdmin={isAdmin}
          />
          <TournamentParticipantsList
            tournament={tournament}
            user={user}
            registrations={registrations}
            userDetails={userDetails}
            locationRankings={locationRankings}
            isAdmin={isAdmin}
          />
        </div>
      </CollapsibleSection>

      {isAdmin && location?.usesRankings && (
        <CollapsibleSection title="Équipes">
          <div className="p-4 space-y-4">
            <TournamentTeamManager
              tournament={tournament}
              registrations={registrations}
              locationRankings={locationRankings}
            />
            {Object.keys(tournament.teams || {}).length > 0 && (
              <TeamsTables
                teams={tournament.teams}
                tournament={tournament}
                locationRankings={locationRankings}
                registrations={registrations}
              />
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default Tournament;
